import { useMemo } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { Box } from "@mui/material";

import { useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { playbackDebugSelectors } from "./store/selectors.debug";
import { playbackSelectors } from "./store/selectors.playback";
import { caseViewerSelectors } from "./store/selectors";

import { PlaybackDeviceDebugStatistics } from "./PlaybackDeviceDebugStatistics";
import { ForwardAndReverseRecordingsPlayer } from "./ForwardAndReverseRecordingsPlayer";
import { ForwardRecordingPlayer } from "./ForwardRecordingPlayer";
import { EmptyRecordingPlayer } from "./EmptyRecordingPlayer";
import { DossierCapabilityName } from "types/DossierCapabilityName";

export interface PlaybackDeviceProps {
    dossierId: EntityId;
    dossierDeviceId: EntityId;
    dossierCapabilityNames: DossierCapabilityName[];
}

export function PlaybackDevice(props: PlaybackDeviceProps) {
    // get recordings for this device from store
    const selectPersistedRecordingsByDossierDeviceId = useMemo(
        () =>
            playbackSelectors.selectPersistedRecordingsByDossierDeviceIdFactory(),
        [],
    );
    const persistedRecordings = useAppSelector((state: RootState) =>
        selectPersistedRecordingsByDossierDeviceId(
            state,
            props.dossierDeviceId,
        ),
    );

    const selectPersistedRecordingsByDossierDeviceIdInPlaybackWindow = useMemo(
        () =>
            playbackSelectors.selectPersistedRecordingsByDossierDeviceIdInPlaybackWindowFactory(),
        [],
    );
    const persistedRecordingsByDossierDeviceIdInPlaybackWindow = useAppSelector(
        (state: RootState) =>
            selectPersistedRecordingsByDossierDeviceIdInPlaybackWindow(
                state,
                props.dossierDeviceId,
            ),
    );
    const showStatistics = useAppSelector(
        playbackDebugSelectors.selectShowStatistics,
    );

    const playbackStartDateTime = useAppSelector(
        playbackSelectors.selectPlaybackStartDateTime,
    );

    const dossierDevice = useAppSelector((state) =>
        caseViewerSelectors.selectSelectedDossierDeviceById(
            state,
            props.dossierDeviceId.toString(),
        ),
    );

    const RecordingPlayerComponent = props.dossierCapabilityNames.some(
        (x) => x === DossierCapabilityName.ReversePlaybackUsingReversedVideos,
    )
        ? ForwardAndReverseRecordingsPlayer
        : ForwardRecordingPlayer;

    return (
        <Box>
            {showStatistics && (
                <PlaybackDeviceDebugStatistics
                    dossierDeviceId={props.dossierDeviceId}
                />
            )}
            {dossierDevice &&
                persistedRecordings &&
                persistedRecordings.map((persistedRecording) => (
                    <RecordingPlayerComponent
                        key={persistedRecording.id}
                        persistedRecordingId={persistedRecording.id}
                        originalStartTime={
                            persistedRecording.recording.startTime
                        }
                        dossierId={props.dossierId}
                        dossierDeviceId={persistedRecording.dossierDeviceId}
                        dossierDeviceName={dossierDevice.name}
                        playbackStartDateTime={playbackStartDateTime}
                        dossierCapabilityNames={props.dossierCapabilityNames}
                    />
                ))}
            {dossierDevice && (
                <EmptyRecordingPlayer
                    hidden={
                        persistedRecordingsByDossierDeviceIdInPlaybackWindow &&
                        persistedRecordingsByDossierDeviceIdInPlaybackWindow.length >
                            0
                    }
                    tooltipText="No recordings available"
                    dossierDeviceName={dossierDevice.name}
                />
            )}
        </Box>
    );
}
